/**
 * The typed client for the candidate API (`docs/ARCHITECTURE.md` §6.1).
 *
 * One function per route, every shape imported from `@altamont/shared` so a wire change can
 * never drift between the browser and the Pages Function. Three behaviours are deliberate:
 *
 *  - **401 is retried once**, after forcing a fresh ID token. An anonymous token lasts an
 *    hour and thinking about your answers can easily take longer.
 *  - **GETs are retried** on a network blip or a 5xx, with a short backoff. Phones lose
 *    signal in a parking lot.
 *  - **POSTs are never retried automatically.** A duplicated `accept` or `submit` must be
 *    impossible; a person can always press the button again.
 */

import {
  CandidateRoutes,
  type AcceptAnswerRequest,
  type AcceptAnswerResponse,
  type AcceptConsentRequest,
  type AcceptConsentResponse,
  type ApiErrorBody,
  type CandidateApplicationStateResponse,
  type CreateApplicationRequest,
  type CreateApplicationResponse,
  type DeleteResumeResponse,
  type DiscardAnswerRequest,
  type DiscardAnswerResponse,
  type PublicConfigResponse,
  type StartQuestionsResponse,
  type SubmitApplicationResponse,
  type UpdateInterestsRequest,
  type UpdateInterestsResponse,
  type UploadResumeResponse,
  type UploadUrlRequest,
  type UploadUrlResponse,
} from '@altamont/shared';

import { getAppCheckToken, getIdToken } from './firebase';

/* ------------------------------------------------------------------------------------------------
 * Errors
 * ---------------------------------------------------------------------------------------------- */

/** A non-2xx response, or a request that never reached the server (`status: 0`). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Narrowing helper, safe across bundle boundaries. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Code used when the request never got a reply: offline, DNS, aeroplane mode. */
const NETWORK_ERROR_CODE = 'network_error';

const GENERIC_MESSAGE = 'Something went wrong. Please try that again.';
const SERVER_MESSAGE = 'Something went wrong on our side. Please try that again.';

/**
 * Warm, plain copy for anything that goes wrong. Server messages are never shown as-is —
 * they are written for logs, not for a person on a phone.
 */
export function friendlyErrorMessage(error: unknown): string {
  if (!isApiError(error)) return GENERIC_MESSAGE;

  switch (error.code) {
    case NETWORK_ERROR_CODE:
      return "We couldn't reach Altamont just then. Check your connection and try again.";
    case 'application_closed':
      return "Altamont isn't taking new video applications right now. Please check back soon.";
    case 'payload_too_large':
      return 'That file is too large. Please choose one under 10 MB.';
    case 'unsupported_media_type':
      return 'We can take a PDF, a Word document, or a photo (JPG or PNG).';
    case 'conflict':
      return 'That step was already saved. Please carry on from here.';
    case 'not_found':
      return "We couldn't find your video application. Please start again from the beginning.";
    case 'rate_limited':
      return 'That was a lot at once. Wait a moment, then try again.';
    case 'bad_request':
      return "Something in that didn't look right. Please check it and try again.";
    case 'unauthorized':
    case 'forbidden':
    case 'app_check_failed':
      return 'Your session has expired. Please start again from the beginning.';
    default:
      return error.status >= 500 ? SERVER_MESSAGE : GENERIC_MESSAGE;
  }
}

function defaultCodeForStatus(status: number): string {
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 405) return 'method_not_allowed';
  if (status === 409) return 'conflict';
  if (status === 413) return 'payload_too_large';
  if (status === 415) return 'unsupported_media_type';
  if (status === 429) return 'rate_limited';
  return status >= 500 ? 'internal_error' : 'bad_request';
}

function parseErrorBody(status: number, text: string): ApiError {
  let code = defaultCodeForStatus(status);
  let message = `Request failed with status ${status}.`;
  let details: unknown;
  try {
    const body = JSON.parse(text) as Partial<ApiErrorBody>;
    const inner = body.error;
    if (inner && typeof inner === 'object') {
      if (typeof inner.code === 'string' && inner.code) code = inner.code;
      if (typeof inner.message === 'string' && inner.message) message = inner.message;
      details = inner.details;
    }
  } catch {
    // Not JSON (an edge/proxy error page). The status-derived code is enough.
  }
  return new ApiError(status, code, message, details);
}

/* ------------------------------------------------------------------------------------------------
 * Transport
 * ---------------------------------------------------------------------------------------------- */

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface RequestOptions {
  /** JSON request body. Sets `Content-Type: application/json`. */
  body?: unknown;
  /** Skip the `Authorization` header. Only `GET /api/config` is public. */
  anonymous?: boolean;
  /** Lets a view abandon a request when someone navigates away. */
  signal?: AbortSignal;
}

/** Attempts for an idempotent GET: the first try plus two retries. */
const GET_ATTEMPTS = 3;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function buildHeaders(
  options: RequestOptions,
  extra: Record<string, string>,
): Promise<Headers> {
  const headers = new Headers(extra);
  headers.set('Accept', 'application/json');

  if (!options.anonymous) {
    headers.set('Authorization', `Bearer ${await getIdToken()}`);
  }

  const appCheckToken = await getAppCheckToken();
  if (appCheckToken) headers.set('X-Firebase-AppCheck', appCheckToken);

  return headers;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const idempotent = method === 'GET';
  const maxAttempts = idempotent ? GET_ATTEMPTS : 1;

  let attempt = 0;
  let refreshedToken = false;

  // Bounded on both sides: `attempt` caps transport retries, `refreshedToken` caps the
  // single 401 retry, so this loop always terminates.
  for (;;) {
    const headers = await buildHeaders(
      options,
      options.body === undefined ? {} : { 'Content-Type': 'application/json' },
    );

    attempt += 1;
    let response: Response;
    try {
      response = await fetch(path, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        credentials: 'omit',
        signal: options.signal,
      });
    } catch (cause) {
      if (options.signal?.aborted) throw cause;
      if (attempt < maxAttempts) {
        await delay(300 * attempt);
        continue;
      }
      throw new ApiError(0, NETWORK_ERROR_CODE, 'The request never reached Altamont.', cause);
    }

    if (response.status === 401 && !refreshedToken && !options.anonymous) {
      // The anonymous token aged out mid-application. Refresh and try exactly once more.
      refreshedToken = true;
      await getIdToken(true);
      continue;
    }

    if (response.status >= 500 && idempotent && attempt < maxAttempts) {
      await delay(300 * attempt);
      continue;
    }

    if (!response.ok) {
      throw parseErrorBody(response.status, await response.text().catch(() => ''));
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}

/* ------------------------------------------------------------------------------------------------
 * Endpoints
 * ---------------------------------------------------------------------------------------------- */

/** `GET /api/config` — copy, work interests, question count, the host's videos. Public. */
export function fetchConfig(signal?: AbortSignal): Promise<PublicConfigResponse> {
  return request<PublicConfigResponse>('GET', CandidateRoutes.config(), {
    anonymous: true,
    signal,
  });
}

/**
 * `POST /api/applications` — starts an application from a name and an email address, the only two
 * things anyone is asked to type. Never retried: it creates a record.
 */
export function createApplication(
  body: CreateApplicationRequest,
): Promise<CreateApplicationResponse> {
  return request<CreateApplicationResponse>('POST', CandidateRoutes.applications(), { body });
}

/** `GET /api/applications/:id` — the state needed to pick the flow back up after a refresh. */
export function fetchApplicationState(
  applicationId: string,
): Promise<CandidateApplicationStateResponse> {
  return request<CandidateApplicationStateResponse>(
    'GET',
    CandidateRoutes.application(applicationId),
  );
}

/** `PATCH /api/applications/:id/interests` — the kinds of work someone chose. */
export function updateInterests(
  applicationId: string,
  body: UpdateInterestsRequest,
): Promise<UpdateInterestsResponse> {
  return request<UpdateInterestsResponse>(
    'PATCH',
    CandidateRoutes.applicationInterests(applicationId),
    { body },
  );
}

/**
 * Header values must be ASCII, and a phone will happily hand us `Résumé (final).pdf`.
 * The server treats this as a hint only — it derives the stored extension from the bytes.
 */
function headerSafeFileName(name: string): string {
  const cleaned = name.replace(/[^\x20-\x7E]/g, '_').replace(/["\\]/g, '_');
  return cleaned.slice(0, 200) || 'upload';
}

/**
 * `POST /api/applications/:id/resume` — the raw file, with its name in `X-File-Name`.
 *
 * Uses `XMLHttpRequest` rather than `fetch` for one reason: only XHR reports upload
 * progress, and a resume on a slow phone connection needs a bar. `onProgress` gets a
 * fraction from 0 to 1.
 */
export async function uploadResume(
  applicationId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResumeResponse> {
  return sendResume(applicationId, file, onProgress, signal, false);
}

async function sendResume(
  applicationId: string,
  file: File,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
  refreshedToken: boolean,
): Promise<UploadResumeResponse> {
  const token = await getIdToken(refreshedToken);
  const appCheckToken = await getAppCheckToken();
  const path = CandidateRoutes.applicationResume(applicationId);

  const result = await new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-File-Name', headerSafeFileName(file.name));
    if (appCheckToken) xhr.setRequestHeader('X-Firebase-AppCheck', appCheckToken);

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      if (onProgress && event.lengthComputable && event.total > 0) {
        onProgress(Math.min(1, event.loaded / event.total));
      }
    };
    xhr.onload = (): void => {
      resolve({ status: xhr.status, text: xhr.responseText });
    };
    xhr.onerror = (): void => {
      reject(new ApiError(0, NETWORK_ERROR_CODE, 'The upload never reached Altamont.'));
    };
    xhr.ontimeout = (): void => {
      reject(new ApiError(0, NETWORK_ERROR_CODE, 'The upload timed out.'));
    };
    xhr.onabort = (): void => {
      reject(new ApiError(0, 'aborted', 'The upload was cancelled.'));
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    xhr.send(file);
  });

  if (result.status === 401 && !refreshedToken) {
    // Nothing was stored, so re-sending after a refresh cannot duplicate anything.
    return sendResume(applicationId, file, onProgress, signal, true);
  }
  if (result.status < 200 || result.status >= 300) {
    throw parseErrorBody(result.status, result.text);
  }

  onProgress?.(1);
  return JSON.parse(result.text) as UploadResumeResponse;
}

/** `DELETE /api/applications/:id/resume`. */
export function deleteResume(applicationId: string): Promise<DeleteResumeResponse> {
  return request<DeleteResumeResponse>('DELETE', CandidateRoutes.applicationResume(applicationId));
}

/** `POST /api/applications/:id/consent` — explicit agreement to a specific disclosure version. */
export function acceptConsent(
  applicationId: string,
  body: AcceptConsentRequest,
): Promise<AcceptConsentResponse> {
  return request<AcceptConsentResponse>('POST', CandidateRoutes.applicationConsent(applicationId), {
    body,
  });
}

/** `POST /api/applications/:id/questions/start` — freezes the question set (idempotent server-side). */
export function startQuestions(applicationId: string): Promise<StartQuestionsResponse> {
  return request<StartQuestionsResponse>(
    'POST',
    CandidateRoutes.applicationQuestionsStart(applicationId),
  );
}

/** `POST /api/applications/:id/answers/:questionId/upload-url` — a one-time Stream TUS endpoint. */
export function createAnswerUploadUrl(
  applicationId: string,
  questionId: string,
  body: UploadUrlRequest,
): Promise<UploadUrlResponse> {
  return request<UploadUrlResponse>(
    'POST',
    CandidateRoutes.answerUploadUrl(applicationId, questionId),
    { body },
  );
}

/** `POST /api/applications/:id/answers/:questionId/accept` — the take someone chose to keep. */
export function acceptAnswer(
  applicationId: string,
  questionId: string,
  body: AcceptAnswerRequest,
): Promise<AcceptAnswerResponse> {
  return request<AcceptAnswerResponse>(
    'POST',
    CandidateRoutes.answerAccept(applicationId, questionId),
    { body },
  );
}

/** `POST /api/applications/:id/answers/:questionId/discard` — throw away an abandoned take. */
export function discardAnswer(
  applicationId: string,
  questionId: string,
  body: DiscardAnswerRequest,
): Promise<DiscardAnswerResponse> {
  return request<DiscardAnswerResponse>(
    'POST',
    CandidateRoutes.answerDiscard(applicationId, questionId),
    { body },
  );
}

/** `POST /api/applications/:id/submit` — the last step. Never retried automatically. */
export function submitApplication(applicationId: string): Promise<SubmitApplicationResponse> {
  return request<SubmitApplicationResponse>(
    'POST',
    CandidateRoutes.applicationSubmit(applicationId),
  );
}
